import { FilterMode } from "@/shared/lib/filter/filter";
import t from "@/lang/lang";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { Input } from "@/shared/components/ui/input";
import { OrderBySelect } from "@/shared/components/tables/order-by-select";
import { formatDateTime } from "@/shared/lib/utilities/date";
import { CustomerFilter } from "@/services/backend/customers/components/customer-filter";
import { CpySourceDialog } from "./cpy-source-dialog";
import {
  SourcesRequest,
  useCpySourcesQuery,
} from "@/services/backend/cpy/api/cpy";
import { DocumentStateBadge } from "@/shared/bsm/core/document/components/ui/document-state-badge";

export function CpySourcesTable({
  request,
  destinationId,
  onCopySuccess,
}: {
  request: SourcesRequest;
  destinationId: string;
  onCopySuccess?: () => void;
}) {
  const { data, isLoading, error } = useCpySourcesQuery(request);

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("Dokument")}</TableHead>
          <TableHead>{t("Nummer / Beschreibung")}</TableHead>
          <TableHead>{t("Status")}</TableHead>
          <TableHead>{t("Erstellt am")}</TableHead>
          <TableHead>{t("Aktualisiert am")}</TableHead>
          <TableHead />
        </TableRow>
      </TableHeader>
      <TableBody>
        {error && (
          <TableRtkErrorRow error={error} colSpan={5} showValidationErrors />
        )}
        {isLoading && <TableLoadingRow colSpan={5} />}
        {data && data.items.length === 0 && <TableEmptyRow colSpan={5} />}
        {data?.items
          .filter((i) => i.id !== destinationId)
          .map((source) => (
            <TableRow key={source.id}>
              <TableCell className="py-0">{t(source.entityName)}</TableCell>
              <TableCell className="py-0">{source.name}</TableCell>
              <TableCell className="py-0">
                {source.data?.state && (
                  <DocumentStateBadge document={{ state: source.data.state }} />
                )}
              </TableCell>
              <TableCell className="py-0">
                {source.data && formatDateTime(source.data.createdAt)}
              </TableCell>
              <TableCell className="py-0">
                {source.data && formatDateTime(source.data.updatedAt)}
              </TableCell>
              <TableCell className="py-0 text-right">
                <CpySourceDialog
                  destinationEntityName={request.destinationEntityName}
                  destinationId={destinationId}
                  sourceEntityName={source.entityName}
                  sourceId={source.id}
                  onSuccess={onCopySuccess}
                />
              </TableCell>
            </TableRow>
          ))}
      </TableBody>
    </Table>
  );
}

export const SoureSearchField = {
  ReferenceNumber: "referenceNumber",
  Name: "name",
  None: "",
};

export type SourceSearchField =
  (typeof SoureSearchField)[keyof typeof SoureSearchField];

export type CpySourcesTableFilterOptions = {
  searchField?: SourceSearchField;
  hideCustomerFilter?: boolean;
  hideOrderBy?: boolean;
};

export function CpySourcesTableFilters({
  searchField = SoureSearchField.ReferenceNumber,
  request,
  onRequestChange,
  hideCustomerFilter = false,
  hideOrderBy = false,
}: {
  request: SourcesRequest;
  onRequestChange: (request: SourcesRequest) => void;
} & CpySourcesTableFilterOptions) {
  return (
    <div className="flex gap-2">
      {searchField == SoureSearchField.ReferenceNumber && (
        <Input
          value={request.params.referenceNumber?.values?.[0] ?? ""}
          onChange={(e) =>
            onRequestChange({
              ...request,
              params: {
                ...request.params,
                referenceNumber: {
                  active: e.target.value.length > 0,
                  mode: FilterMode.Like,
                  values: [e.target.value],
                },
              },
            })
          }
          placeholder={t("Dokumentennummer")}
          className="h-8"
        />
      )}
      {searchField == SoureSearchField.Name && (
        <Input
          value={request.params.name?.values?.[0] ?? ""}
          onChange={(e) =>
            onRequestChange({
              ...request,
              params: {
                ...request.params,
                name: {
                  ...request.params.name,
                  active: e.target.value.length > 0,
                  values: [e.target.value],
                },
              },
            })
          }
          placeholder={t("Name")}
          className="h-8"
        />
      )}
      {!hideCustomerFilter && (
        <CustomerFilter
          filter={request.params.customer}
          onFilterChange={(customer) =>
            onRequestChange({
              ...request,
              params: { ...request.params, customer },
            })
          }
        />
      )}
      {!hideOrderBy && (
        <OrderBySelect<"createdAt" | "updatedAt">
          orderBy={request.params.orderBy}
          onOrderByChange={(orderBy) =>
            onRequestChange({
              ...request,
              params: { ...request.params, orderBy },
            })
          }
          fields={[
            { label: "Erstellt am", value: "createdAt" },
            { label: "Aktualisiert am", value: "updatedAt" },
          ]}
        />
      )}
    </div>
  );
}
